import { useState, useEffect, useRef, useCallback } from 'react';
import { getGoogleMapsLoader } from '@/utils/googleMapsLoader';
import { MapPosition } from '../types';

interface UseGoogleMapsOptions {
  onReady?: () => void;
}

export const useGoogleMaps = (options?: UseGoogleMapsOptions) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadMaps = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await getGoogleMapsLoader();
        
        if (mounted) {
          setIsLoaded(true);
          setIsLoading(false);
          options?.onReady?.();
        }
      } catch (err) {
        console.error('[useGoogleMaps] Failed to load:', err);
        if (mounted) {
          setError('Failed to load Google Maps. Please check your connection.');
          setIsLoading(false);
        }
      }
    };

    loadMaps();

    return () => {
      mounted = false;
    };
  }, []);

  const retry = useCallback(() => {
    setIsLoading(true);
    setError(null);
    getGoogleMapsLoader()
      .then(() => {
        setIsLoaded(true);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('[useGoogleMaps] Retry failed:', err);
        setError('Failed to load Google Maps. Please try again.');
        setIsLoading(false);
      });
  }, []);

  return { isLoaded, isLoading, error, retry };
};

// Helper to find the bounding box of non-transparent pixels
const getContentBounds = (ctx: CanvasRenderingContext2D, width: number, height: number) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  let minX = width, minY = height, maxX = 0, maxY = 0;
  
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const alpha = data[(y * width + x) * 4 + 3];
      if (alpha > 10) { // Non-transparent pixel
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  
  // Add small padding
  const pad = 2;
  return {
    x: Math.max(0, minX - pad),
    y: Math.max(0, minY - pad),
    width: Math.min(width, maxX - minX + pad * 2),
    height: Math.min(height, maxY - minY + pad * 2)
  };
};

// Module-level icon cache: deduplicates canvas processing for identical icon+border combos
const markerIconCache = new Map<string, Promise<string>>();

/** Clear the marker icon cache (e.g. on theme change or cleanup) */
export const clearMarkerIconCache = () => {
  markerIconCache.clear();
};

// Cached wrapper for createIconWithBackground
const _createIconWithBackground = (iconUrl: string, borderColor: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Use device pixel ratio for crisp rendering on retina displays
      const dpr = window.devicePixelRatio || 1;
      const displaySize = 80;
      const size = displaySize * dpr;
      
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      canvas.style.width = `${displaySize}px`;
      canvas.style.height = `${displaySize}px`;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        // Scale context for high DPI
        ctx.scale(dpr, dpr);
        
        // Draw white circular background for visibility on any map
        ctx.fillStyle = '#ffffff';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        ctx.beginPath();
        ctx.arc(displaySize / 2, displaySize / 2, displaySize / 2 - 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw colored border
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Reset shadow for icon drawing
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
        
        // Enable high-quality image smoothing for crisp downscaling of high-res SVGs
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Create a temporary canvas to find content bounds (trim transparent padding)
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = img.width;
        tempCanvas.height = img.height;
        const tempCtx = tempCanvas.getContext('2d');
        
        if (tempCtx) {
          // Draw original image to analyze bounds
          tempCtx.drawImage(img, 0, 0);
          
          // Get content bounds (trim transparent padding)
          const bounds = getContentBounds(tempCtx, img.width, img.height);
          
          // Calculate scaling based on content bounds, not full image
          const padding = 14;
          const availableSize = displaySize - padding * 2;
          const contentWidth = bounds.width > 0 ? bounds.width : img.width;
          const contentHeight = bounds.height > 0 ? bounds.height : img.height;
          const scale = Math.min(availableSize / contentWidth, availableSize / contentHeight);
          const scaledWidth = contentWidth * scale;
          const scaledHeight = contentHeight * scale;
          
          // Center the cropped content
          const x = (displaySize - scaledWidth) / 2;
          const y = (displaySize - scaledHeight) / 2;
          
          // Draw the icon directly without darkening overlay for maximum clarity
          ctx.drawImage(
            tempCanvas, 
            bounds.x, bounds.y, contentWidth, contentHeight,
            x, y, scaledWidth, scaledHeight
          );
        } else {
          // Fallback: draw original image centered
          const padding = 12;
          const availableSize = displaySize - padding * 2;
          const scale = Math.min(availableSize / img.width, availableSize / img.height);
          const scaledWidth = img.width * scale;
          const scaledHeight = img.height * scale;
          const x = (displaySize - scaledWidth) / 2;
          const y = (displaySize - scaledHeight) / 2;
          ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
        }
      }
      
      resolve(canvas.toDataURL('image/png', 1.0));
    };
    img.onerror = () => {
      resolve(iconUrl);
    };
    img.src = iconUrl;
  });
};

// Helper to create properly scaled icon with white background - HIGH DPI support for sharp rendering
// Uses cache to avoid redundant canvas processing for same icon+border combinations
export const createIconWithBackground = (iconUrl: string, borderColor: string): Promise<string> => {
  const cacheKey = `icon::${iconUrl}::${borderColor}`;
  const cached = markerIconCache.get(cacheKey);
  if (cached) return cached;
  const promise = _createIconWithBackground(iconUrl, borderColor);
  markerIconCache.set(cacheKey, promise);
  return promise;
};

// Helper to create hero image marker that fills the circle (like a profile picture)
const _createHeroImageMarker = (imageUrl: string, borderColor: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
img.onload = () => {
      // Use device pixel ratio for crisp rendering on retina displays
      const dpr = window.devicePixelRatio || 1;
      const displaySize = 80;
      const borderWidth = 3;
      
      const canvas = document.createElement('canvas');
      canvas.width = displaySize * dpr;
      canvas.height = displaySize * dpr;
      canvas.style.width = `${displaySize}px`;
      canvas.style.height = `${displaySize}px`;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        // Scale context for high DPI
        ctx.scale(dpr, dpr);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        ctx.save();
        
        // Add shadow for depth
        ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        
        // Draw a filled circle first to create the shadow
        ctx.beginPath();
        ctx.arc(displaySize / 2, displaySize / 2, (displaySize / 2) - borderWidth, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        
        // Reset shadow before clipping
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
        
        // Create circular clipping path
        ctx.beginPath();
        ctx.arc(displaySize / 2, displaySize / 2, (displaySize / 2) - borderWidth, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        
        // Calculate "cover" style sizing - fill circle completely
        const circleSize = displaySize - (borderWidth * 2);
        const imgAspect = img.width / img.height;
        let drawWidth, drawHeight, drawX, drawY;
        
        if (imgAspect > 1) {
          // Image is wider - fit height, crop sides
          drawHeight = circleSize;
          drawWidth = circleSize * imgAspect;
          drawX = borderWidth + (circleSize - drawWidth) / 2;
          drawY = borderWidth;
        } else {
          // Image is taller - fit width, crop top/bottom
          drawWidth = circleSize;
          drawHeight = circleSize / imgAspect;
          drawX = borderWidth;
          drawY = borderWidth + (circleSize - drawHeight) / 2;
        }
        
        // Draw the image (clipped to circle)
        ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
        
        ctx.restore();
        
        // Draw border on top
        ctx.beginPath();
        ctx.arc(displaySize / 2, displaySize / 2, (displaySize / 2) - (borderWidth / 2), 0, Math.PI * 2);
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        ctx.stroke();
      }
      
      resolve(canvas.toDataURL('image/png', 1.0));
    };
    img.onerror = () => {
      resolve(imageUrl);
    };
    img.src = imageUrl;
  });
};

export const createHeroImageMarker = (imageUrl: string, borderColor: string): Promise<string> => {
  const cacheKey = `hero::${imageUrl}::${borderColor}`;
  const cached = markerIconCache.get(cacheKey);
  if (cached) return cached;
  const promise = _createHeroImageMarker(imageUrl, borderColor);
  markerIconCache.set(cacheKey, promise);
  return promise;
};

// Helper to create logo marker that fits the entire logo inside the circle (for company logos)
// Uses "contain" sizing to ensure full logo visibility with proper padding
const _createLogoMarker = (imageUrl: string, borderColor: string): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      // Use device pixel ratio for crisp rendering on retina displays
      const dpr = window.devicePixelRatio || 1;
      const displaySize = 80;
      const size = displaySize * dpr;
      const borderWidth = 3;
      
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      canvas.style.width = `${displaySize}px`;
      canvas.style.height = `${displaySize}px`;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        // Scale context for high DPI
        ctx.scale(dpr, dpr);
        ctx.save();
        
        // Draw white circular background with shadow
        ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetY = 2;
        ctx.beginPath();
        ctx.arc(displaySize / 2, displaySize / 2, (displaySize / 2) - borderWidth, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        
        // Reset shadow and create clipping path
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
        ctx.beginPath();
        ctx.arc(displaySize / 2, displaySize / 2, (displaySize / 2) - borderWidth, 0, Math.PI * 2);
        ctx.clip();
        
        // Enable high-quality image smoothing for crisp downscaling
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Calculate "contain" style sizing with padding for full logo visibility
        const padding = 10; // Padding for logos
        const availableSize = displaySize - (borderWidth * 2) - (padding * 2);
        const scale = Math.min(availableSize / img.width, availableSize / img.height);
        const scaledWidth = img.width * scale;
        const scaledHeight = img.height * scale;
        const drawX = (displaySize - scaledWidth) / 2;
        const drawY = (displaySize - scaledHeight) / 2;
        
        // Draw the logo (contained within circle)
        ctx.drawImage(img, drawX, drawY, scaledWidth, scaledHeight);
        
        ctx.restore();
        
        // Draw colored border on top
        ctx.beginPath();
        ctx.arc(displaySize / 2, displaySize / 2, (displaySize / 2) - (borderWidth / 2), 0, Math.PI * 2);
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = borderWidth;
        ctx.stroke();
      }
      
      resolve(canvas.toDataURL('image/png', 1.0));
    };
    img.onerror = () => resolve(imageUrl);
    img.src = imageUrl;
  });
};

export const createLogoMarker = (imageUrl: string, borderColor: string): Promise<string> => {
  const cacheKey = `logo::${imageUrl}::${borderColor}`;
  const cached = markerIconCache.get(cacheKey);
  if (cached) return cached;
  const promise = _createLogoMarker(imageUrl, borderColor);
  markerIconCache.set(cacheKey, promise);
  return promise;
};

// Map styles to hide POIs
export const defaultMapStyles: google.maps.MapTypeStyle[] = [
  {
    featureType: 'poi',
    elementType: 'labels',
    stylers: [{ visibility: 'off' }]
  }
];

// Dark mode map styles
export const darkMapStyles: google.maps.MapTypeStyle[] = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#384e7a' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#2c6675' }] },
  { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#2f3948' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
];

// Calculate bounds from positions
export const calculateBounds = (positions: MapPosition[]): google.maps.LatLngBounds | null => {
  if (positions.length === 0 || typeof google === 'undefined') return null;
  
  const bounds = new google.maps.LatLngBounds();
  positions.forEach(pos => {
    bounds.extend(new google.maps.LatLng(pos.lat, pos.lng));
  });
  return bounds;
};

// Convert miles to meters
export const milesToMeters = (miles: number): number => miles * 1609.34;
